// macadamia-app/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { NextStep, isNextStep } from '../routing/nextStep';

export interface User {
  id: string;
  profilePicture: string;
  name: string;
  email: string;
  role?: 'admin' | 'user';
  verifiedUser?: boolean;
  plan?: string;
  profilePictureUrl?: string;
  profilePictureTimestamp?: number;
  profilePictureCache?: string;
  bio?: string;
  websiteUrl?: string;
  linkedinUrl?: string;
  publicEmail?: string;
  publicProfile?: boolean;
  category?: string;
  subcategory?: string;
  hasOnboardingInterests?: boolean;
  onboardingInterests?: string[];
  nextStep?: NextStep;
}

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  login: (user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshProfilePicture: (sizePreset?: string) => Promise<string | null>;
  getAuthorProfilePicture: (userId: string, sizePreset?: string) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cache pour les URLs des photos de profil des auteurs
const authorProfilePictureCache: Record<string, { url: string, timestamp: number }> = {};

export const AuthProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const localUser = localStorage.getItem('user');
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch {
        localStorage.removeItem('user');
      }
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const skipValidationRef = useRef(false);
  const navigate = useNavigate();

  // Ref pour stocker l'URL de la photo de profil (stabilise clearAuth)
  const userProfilePictureUrlRef = useRef<string | undefined>(user?.profilePictureUrl);

  // Synchroniser la ref quand user.profilePictureUrl change
  useEffect(() => {
    userProfilePictureUrlRef.current = user?.profilePictureUrl;
  }, [user?.profilePictureUrl]);

  const clearAuth = useCallback(async () => {
    // Révoquer la blob URL de la photo de profil de l'utilisateur actuel
    if (userProfilePictureUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(userProfilePictureUrlRef.current);
    }

    // Révoquer les blob URLs du cache des auteurs
    Object.values(authorProfilePictureCache).forEach(entry => {
      if (entry.url?.startsWith('blob:')) {
        URL.revokeObjectURL(entry.url);
      }
    });

    // Nettoyer le cookie httpOnly côté serveur
    try {
      await apiClient('/users/logout', { method: 'POST' });
    } catch {
      // Ignorer l'erreur si le serveur est injoignable
    }

    // Nettoyer les anciennes clés localStorage (migration)
    localStorage.removeItem('__auth_t');
    localStorage.removeItem('__app_key');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    Object.keys(authorProfilePictureCache).forEach(key => {
      delete authorProfilePictureCache[key];
    });

    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  }, []);

  const login = useCallback((newUser: User): void => {
    localStorage.setItem('user', JSON.stringify(newUser));

    // Nettoyer les anciennes clés localStorage (migration)
    localStorage.removeItem('__auth_t');
    localStorage.removeItem('__app_key');
    localStorage.removeItem('token');

    setUser(newUser);
    setIsAuthenticated(true);
    setIsLoading(false);
    skipValidationRef.current = true;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    navigate('/login');
  }, [clearAuth, navigate]);

  const checkTokenValidity = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiClient('/users/me');

      if (!response.ok) {
        // Compte supprimé : déconnexion silencieuse
        if (response.status === 401) {
          try {
            const errorData = await response.json();
            if (errorData.code === 'ACCOUNT_DELETED') {
              return false;
            }
          } catch {}
        }
        return false;
      }

      const userData = await response.json();

      const updatedUser = {
        id: userData._id,
        profilePicture: userData.profilePicture || '',
        profilePictureUrl: userData.profilePictureUrl || undefined,
        profilePictureTimestamp: userData.profilePictureUrl ? Date.now() : undefined,
        name: `${userData.firstName} ${userData.lastName}`,
        email: userData.email,
        verifiedUser: userData.verifiedUser,
        plan: userData.plan,
        hasOnboardingInterests: userData.onboardingInterests !== undefined
          ? (Array.isArray(userData.onboardingInterests) && userData.onboardingInterests.length > 0)
          : (user?.hasOnboardingInterests ?? false),
        onboardingInterests: Array.isArray(userData.onboardingInterests)
          ? userData.onboardingInterests
          : (user?.onboardingInterests ?? []),
        nextStep: isNextStep(userData.nextStep) ? userData.nextStep : user?.nextStep,
      };

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify({
        ...updatedUser,
        profilePictureUrl: undefined,
        profilePictureTimestamp: undefined,
      }));

      return true;
    } catch (error) {
      console.error('Erreur validation session:', error);
      return false;
    }
  }, []);

  const refreshProfilePicture = useCallback(async (sizePreset = 'avatar') => {
    if (!user?.profilePicture || !isAuthenticated) return null;

    const now = Date.now();
    const cacheKey = `${user.profilePicture}-${sizePreset}`;

    if (
      user.profilePictureUrl &&
      user.profilePictureTimestamp &&
      user.profilePictureCache === cacheKey &&
      now - user.profilePictureTimestamp < 5 * 60 * 1000
    ) {
      return user.profilePictureUrl;
    }

    try {
      const response = await apiClient(
        `/users/refresh-profile-picture?preset=${sizePreset}`
      );

      if (!response.ok) {
        if (response.status === 401) {
          return null;
        }

        if (response.status === 404) {
          console.warn('Photo de profil 404, clé conservée pour retry ultérieur');
          const updatedUser = {
            ...user,
            profilePictureUrl: undefined,
            profilePictureTimestamp: undefined,
            profilePictureCache: undefined,
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return null;
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.startsWith('image/')) {
        const blob = await response.blob();

        if (user.profilePictureUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(user.profilePictureUrl);
        }

        const imageUrl = URL.createObjectURL(blob);

        const updatedUser = {
          ...user,
          profilePictureUrl: imageUrl,
          profilePictureTimestamp: Date.now(),
          profilePictureCache: cacheKey,
        };

        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify({
          ...updatedUser,
          profilePictureUrl: undefined,
        }));

        return imageUrl;
      }

      const data = await response.json();
      if (!data.profilePicture) {
        console.warn('Pas de profilePicture dans la réponse, clé conservée');
        const updatedUser = {
          ...user,
          profilePictureUrl: undefined,
          profilePictureTimestamp: undefined,
          profilePictureCache: undefined,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return null;
      }

      const updatedUser = {
        ...user,
        profilePictureUrl: data.profilePicture,
        profilePictureTimestamp: Date.now(),
        profilePictureCache: cacheKey,
      };

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return data.profilePicture;
    } catch (error) {
      console.error("Erreur lors du rafraîchissement de la photo de profil:", error);

      const updatedUser = {
        ...user,
        profilePictureUrl: undefined,
        profilePictureTimestamp: undefined,
        profilePictureCache: undefined,
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return null;
    }
  }, [user, isAuthenticated, logout]);

  const getAuthorProfilePicture = useCallback(async (userId: string, sizePreset = 'thumbnail') => {
    if (!isAuthenticated) return null;

    if (!/^[a-zA-Z0-9_-]+$/.test(userId)) {
      console.error('ID utilisateur invalide:', userId);
      return null;
    }

    const now = Date.now();
    const cacheKey = `${userId}-${sizePreset}`;

    if (
      authorProfilePictureCache[cacheKey] &&
      now - authorProfilePictureCache[cacheKey].timestamp < 13 * 60 * 1000
    ) {
      return authorProfilePictureCache[cacheKey].url;
    }

    try {
      const response = await apiClient(
        `/users/${encodeURIComponent(userId)}/profile-picture?preset=${sizePreset}`
      );

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return null;
        }

        if (response.status === 404) {
          return null;
        }

        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.startsWith('image/')) {
        const blob = await response.blob();

        if (authorProfilePictureCache[cacheKey]?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(authorProfilePictureCache[cacheKey].url);
        }

        const imageUrl = URL.createObjectURL(blob);

        authorProfilePictureCache[cacheKey] = {
          url: imageUrl,
          timestamp: now
        };
        return imageUrl;
      }

      const data = await response.json();
      if (data.profilePictureUrl) {
        authorProfilePictureCache[cacheKey] = {
          url: data.profilePictureUrl,
          timestamp: now
        };
        return data.profilePictureUrl;
      }

      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération de la photo de profil de l'auteur:", error);
      return null;
    }
  }, [isAuthenticated, logout]);

  // EFFET PRINCIPAL - Validation de la session au démarrage via cookie
  useEffect(() => {
    const validateSession = async () => {
      if (skipValidationRef.current) {
        setIsLoading(false);
        return;
      }

      try {
        const isValid = await checkTokenValidity();

        if (!isValid) {
          // Nettoyer sans appeler logout (qui clearCookie + navigate)
          localStorage.removeItem('user');
          localStorage.removeItem('__auth_t');
          localStorage.removeItem('__app_key');
          localStorage.removeItem('token');
          setUser(null);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('AuthContext - Erreur lors de la validation:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleAuthChange = (event: CustomEvent<any>) => {
      if (!event.detail.isAuthenticated) {
        logout();
      }
    };

    window.addEventListener('authChange', handleAuthChange as EventListener);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        refreshProfilePicture();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('authChange', handleAuthChange as EventListener);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, logout]);

  // Rafraîchissement périodique de la photo de profil
  useEffect(() => {
    if (!user?.profilePicture || !isAuthenticated) return;

    refreshProfilePicture();

    const refreshInterval = setInterval(() => {
      refreshProfilePicture();
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profilePicture, isAuthenticated]);

  // Vérification périodique de la validité de la session
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSessionPeriodically = setInterval(async () => {
      const isValid = await checkTokenValidity();
      if (!isValid) {
        logout();
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(checkSessionPeriodically);
    };
  }, [isAuthenticated, checkTokenValidity, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        isAuthenticated,
        isLoading,
        refreshProfilePicture,
        getAuthorProfilePicture,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
